// Shared enforced-filter controls: the designer-defined filter-set picker
// (with the always-available "No filter (all records)" bypass) plus the
// collapsible per-condition value bar the end user uses to re-range an
// unlocked enforced filter.
//
// Rendered by BOTH the saved-records list (ListView) and the multi-tabular
// entry view (EmbedCaptureTabularPreview) so the enforced filter stays
// visible and adjustable when the viewer switches interfaces — switching to
// multi-tabular no longer makes the filter "disappear". Keeping it in one
// component avoids the two surfaces drifting apart.
//
// The inputs themselves are VisualCriteriaForm — the same criteria form a
// visual or dashboard prompts with. A filter value is a criterion: it is
// typed to its column's data type, picked from a picklist where the column
// has one, and looked up from a master where the designer configured one.
// Having ONE form build them is what keeps the two surfaces honest about
// what a column accepts.

import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Filter, Info, Lock } from 'lucide-react'

import type { SqlModel } from '../types'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice,
} from '../contracts'
import { filterInputKind } from '../types'
import { columnDbName } from '../utils'
import { useTxnRuntimeOptional } from '../context'
import VisualCriteriaForm, {
  type VisualCriteriaPromptVar, type VisualCriteriaRuntime,
} from '../VisualCriteriaForm'

// Keyed by each condition's own slot key (its column when that column carries
// a single condition — see EnforcedConditionEcho.key).
export type OverrideMap = Record<string, { from?: string; to?: string; value?: string }>

// The value slot one condition owns. Two conditions on the SAME column (a date
// range written as `>` and `<`) each get their own, which is what stops them
// writing over each other.
function slotOf(c: EnforcedConditionEcho): string {
  return c.key || c.column
}

// A range is two values in one criterion, and VisualCriteriaForm carries them
// comma-joined (the shape the BI engine already splits). The enforced filter
// keeps from/to apart on the wire, so the two are converted at this boundary
// rather than teaching either side the other's shape.
const RANGE_SEP = ','

function joinRange(from?: string | null, to?: string | null): string {
  const lo = (from || '').trim()
  const hi = (to || '').trim()
  return lo || hi ? `${lo}${RANGE_SEP}${hi}` : ''
}

function splitRange(v: string | undefined): { from: string; to: string } {
  const parts = (v ?? '').split(RANGE_SEP)
  return { from: (parts[0] ?? '').trim(), to: (parts[1] ?? '').trim() }
}

interface Props {
  model: SqlModel
  /** Active set's resolved conditions (the value bar). */
  enforcedFilters?: EnforcedConditionEcho[]
  /** Designer-defined sets for the picker. */
  filterSets?: EnforcedFilterSetEcho[]
  activeFilterSetId?: string | null
  bypassed?: boolean
  busy?: boolean
  /** Non-blocking messages from the conditions' validations. */
  notices?: FilterNotice[]
  /** Apply the user's edited condition values. */
  onApplyFilters?: (overrides: OverrideMap) => void
  /** Switch the active set, or choose "No filter (all records)". */
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // Project folder + object name — only needed to open a condition's
  // reference picker. Omitted ⇒ conditions still render, without the picker.
  project?: string
  objName?: string
}

export default function EnforcedFilterControls({
  model, enforcedFilters, filterSets, activeFilterSetId, bypassed, busy,
  notices, onApplyFilters, onSelectFilterSet, project, objName,
}: Props) {
  // The lookup transport, when the host wired one (every real transaction
  // surface does; a bare preview may not).
  const rt = useTxnRuntimeOptional()
  const canLookup = !!rt && !!project

  // Fall back to the model's own column definitions for a response that
  // predates the typed echo — an older server still gets typed inputs.
  const kindByDb = useMemo(() => {
    const m = new Map<string, string>()
    for (const cc of model.columns || []) {
      m.set(columnDbName(cc.column).toLowerCase(), filterInputKind(cc.column))
    }
    return m
  }, [model])

  // Local, editable copy of the conditions' values — seeded from the
  // server-resolved echo and re-seeded whenever it changes.
  const echoSig = useMemo(() => JSON.stringify(enforcedFilters || []), [enforcedFilters])
  const [values, setValues] = useState<Record<string, string>>({})
  useEffect(() => {
    const next: Record<string, string> = {}
    for (const c of enforcedFilters || []) {
      next[slotOf(c)] = c.op === 'between'
        ? joinRange(c.from, c.to)
        : (c.value || '')
    }
    setValues(next)
  }, [echoSig])

  // One criterion per condition: the echo already carries the column's data
  // type, its picklist and its reference picker, so nothing here guesses.
  const promptVars = useMemo<VisualCriteriaPromptVar[]>(
    () => (enforcedFilters || []).map((c) => ({
      name: slotOf(c),
      label: `${c.label}${OP_SUFFIX[c.op] ?? ''}`,
      label_from: c.label_from,
      label_to: c.label_to,
      data_type: c.data_type || kindByDb.get(c.column) || 'text',
      operator: c.op,
      options: c.options,
      locked: c.locked,
      placeholder: c.op === 'in' ? 'Add value…' : '',
      ...(canLookup ? {
        lookup: c.lookup,
        lookup_sql: c.lookup_sql,
        lookup_filter: c.lookup_filter,
        lookup_filter_rules: c.lookup_filter_rules as never,
        lookup_title: c.lookup_title,
        lookup_sql_data_col: c.lookup_sql_data_col,
      } : {}),
    })),
    [enforcedFilters, kindByDb, canLookup],
  )

  const criteriaRuntime = useMemo<VisualCriteriaRuntime>(
    () => (rt ? { lookup: rt.lookup, notify: rt.notify } : NO_LOOKUP_RUNTIME),
    [rt],
  )

  const applyFilters = () => {
    const out: OverrideMap = {}
    for (const c of enforcedFilters || []) {
      if (c.locked) continue
      const slot = slotOf(c)
      if (c.op === 'between') {
        const { from, to } = splitRange(values[slot])
        out[slot] = { from, to }
      } else {
        out[slot] = { value: values[slot] || '' }
      }
    }
    onApplyFilters?.(out)
    // Refresh happens via onApplyFilters; collapse the panel so the list is
    // back in view with the new range shown in the summary.
    setFilterOpen(false)
  }
  const hasEditableFilter = (enforcedFilters || []).some((c) => !c.locked)

  // The set picker appears whenever the form declares any filter set and the
  // host wired up selection — even with a single set, since "No filter (all
  // records)" is always offered as a bypass.
  const showSetPicker = !!onSelectFilterSet && !!filterSets && filterSets.length > 0

  // Open or collapsed on arrival: the designer's choice for the active set
  // when they made one, otherwise derived — a set that still needs a value
  // from the user opens with its inputs showing, a set whose values are all
  // resolved opens collapsed with the data already on screen.
  const activeSet = useMemo(
    () => (filterSets || []).find((s) => s.id === activeFilterSetId),
    [filterSets, activeFilterSetId],
  )
  const defaultOpen = useMemo(() => {
    if (typeof activeSet?.expand_inputs === 'boolean') return activeSet.expand_inputs
    return (enforcedFilters || []).some(
      (c) => !c.locked && (c.op === 'between'
        ? !(c.from || '').trim() && !(c.to || '').trim()
        : !(c.value || '').trim()),
    )
  }, [activeSet, enforcedFilters])
  const [filterOpen, setFilterOpen] = useState(defaultOpen)
  // Re-evaluated when the user switches set (or the server re-resolves one) —
  // each set brings its own answer to "does this still need a value?".
  useEffect(() => {
    setFilterOpen(defaultOpen)
  }, [activeFilterSetId, bypassed, defaultOpen])

  const filterSummary = useMemo(
    () => (enforcedFilters || []).map((c) => c.op === 'between'
      ? `${c.label}: ${c.from || '…'} → ${c.to || '…'}`
      : `${c.label}: ${c.value || '—'}`).join('   ·   '),
    [enforcedFilters],
  )

  const hasValueBar = !!enforcedFilters && enforcedFilters.length > 0
  if (!showSetPicker && !hasValueBar) return null

  return (
    <div style={{
      borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-surface-2, var(--color-bg-secondary))',
    }}>
      {/* Single compact bar: the filter-set picker and the collapsible "Refine"
          summary/toggle live on the same row — no stacked rows or inner border. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '7px 16px',
      }}>
        {showSetPicker && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={labelStyle} title="Chooses which saved records are loaded">Showing</span>
            <select
              value={bypassed ? '__bypass__' : (activeFilterSetId || '')}
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value
                if (v === '__bypass__') onSelectFilterSet!({ bypass: true })
                else onSelectFilterSet!({ setId: v })
              }}
              style={{
                padding: '4px 8px', background: 'var(--color-bg)',
                border: '1px solid var(--color-border)', borderRadius: 4,
                color: 'var(--color-text)', fontSize: '0.8425rem',
              }}
            >
              {(filterSets || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              <option value="__bypass__">No filter (all records)</option>
            </select>
          </div>
        )}
        {/* Editable sets get an obvious "Edit filter" button that toggles the
            value panel; fully-locked sets just show their values, read-only. */}
        {hasValueBar && hasEditableFilter && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            minWidth: 0, flex: '1 1 auto',
          }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setFilterOpen((o) => !o)}
              aria-expanded={filterOpen}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
            >
              <Filter size={12} /> Edit filter
              {filterOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            {!filterOpen && filterSummary && (
              <span style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {filterSummary}
              </span>
            )}
          </div>
        )}
        {hasValueBar && !hasEditableFilter && filterSummary && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0,
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <Lock size={11} aria-label="Fixed by the designer" /> {filterSummary}
          </span>
        )}
      </div>
      {/* Non-blocking messages from the conditions' own validations. A
          blocking one never reaches here — it refuses the list instead. */}
      {(notices || []).length > 0 && (
        <div style={{ padding: '0 16px 8px', display: 'grid', gap: 4 }}>
          {notices!.map((n, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: '0.8125rem',
              color: n.severity === 'W' ? 'var(--color-warning, #b26a00)'
                : 'var(--color-text-secondary)',
            }}>
              {n.severity === 'W' ? <AlertTriangle size={12} /> : <Info size={12} />}
              {n.message}
            </div>
          ))}
        </div>
      )}
      {hasValueBar && filterOpen && (
        <div style={{ padding: '0 16px 12px', display: 'grid', gap: 10 }}>
          <VisualCriteriaForm
            project={project || ''}
            objName={objName || ''}
            promptVars={promptVars}
            values={values}
            onChange={(name, v) => setValues((prev) => ({ ...prev, [name]: v }))}
            onSubmit={applyFilters}
            runtime={criteriaRuntime}
          />
          {hasEditableFilter && (
            <div>
              <button
                className="btn btn-secondary btn-sm" disabled={busy}
                onClick={applyFilters}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Stand-in transport for a host that wired none. No prompt var carries lookup
// config in that case (see `canLookup`), so nothing can reach it.
const NO_LOOKUP_RUNTIME: VisualCriteriaRuntime = {
  lookup: () => Promise.reject(new Error(
    'No lookup transport is available for this filter bar.')),
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.8225rem', fontWeight: 600,
  color: 'var(--color-text-secondary)',
}

// Human label suffix per operator so a non-date condition reads naturally,
// e.g. "Status", "Amount ≥", "Name contains".
const OP_SUFFIX: Record<string, string> = {
  eq: '', neq: ' ≠', in: '', gt: ' >', gte: ' ≥', lt: ' <', lte: ' ≤',
  contains: ' contains', starts_with: ' starts with', between: '',
}
